import { useEffect, useMemo, useRef, useState, type ComponentType } from "react";
import { useQuery } from "@tanstack/react-query";
import type { EditorView, ReactCodeMirrorProps } from "@uiw/react-codemirror";
import type { Extension } from "@codemirror/state";
import type { FileContentResponse } from "@rivets/core";
import { readFile } from "@/lib/dataSource";
import { perfMeasure } from "@/lib/perf";
import { qk } from "@/lib/query-keys";
import { Skeleton } from "@/components/Skeleton";
import { EmptyState } from "@/components/EmptyState";
import { useActiveTheme } from "@/lib/theme";
import type { FileLineRange } from "./WorkspaceDocuments";
import { lineBreakOf, saveFileDraft, useFileDraft, useFileDrafts, withLineBreak } from "./fileDraftStore";

type CodeMirrorModule = typeof import("@uiw/react-codemirror");
type CodeMirrorComponent = ComponentType<ReactCodeMirrorProps>;

let cachedCodeMirror: CodeMirrorModule | null = null;
let cachedHighlighting: Extension | null = null;

async function loadCodeMirror(): Promise<CodeMirrorModule> {
  if (!cachedCodeMirror || !cachedHighlighting) {
    const [mod, language, highlight] = await Promise.all([
      import("@uiw/react-codemirror"),
      import("@codemirror/language"),
      import("@lezer/highlight"),
    ]);
    // For a theme other than Rivets: lezer's class names rather than a colour
    // style, so the `tok-*` rules in global.css — the same rules that colour a
    // diff — paint the theme's `--syntax-*` tokens.
    cachedHighlighting = language.syntaxHighlighting(highlight.classHighlighter);
    cachedCodeMirror = mod;
  }
  return cachedCodeMirror;
}

/** The editor chrome in theme tokens. Colours are `var()`s, so a theme change
 * repaints without rebuilding the editor; only `dark` — which picks
 * CodeMirror's own defaults for anything unstyled here — needs a rebuild. */
function editorThemeExtension(mod: CodeMirrorModule, dark: boolean): Extension {
  return [
    mod.EditorView.theme({
      "&": { color: "var(--text-primary)", backgroundColor: "var(--surface-0)" },
      ".cm-content": { caretColor: "var(--text-primary)" },
      ".cm-cursor, .cm-dropCursor": { borderLeftColor: "var(--text-primary)" },
      "&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection": { backgroundColor: "var(--accent-soft)" },
      ".cm-gutters": { backgroundColor: "var(--surface-0)", color: "var(--text-tertiary)", borderRightColor: "var(--border-subtle)" },
      ".cm-activeLineGutter": { backgroundColor: "transparent" },
      ".cm-foldPlaceholder": { backgroundColor: "var(--surface-2)", color: "var(--text-secondary)", border: "none" },
      ".cm-searchMatch": { backgroundColor: "var(--find-match)" },
      ".cm-searchMatch.cm-searchMatch-selected": { backgroundColor: "var(--find-match-current)" },
      ".cm-panels": { backgroundColor: "var(--surface-1)", color: "var(--text-primary)" },
      ".cm-tooltip": { backgroundColor: "var(--surface-1)", border: "1px solid var(--border-strong)" },
    }, { dark }),
    cachedHighlighting ?? [],
  ];
}

/** Marks the lines a transcript link cited, clamped to the file. */
function linkedLinesExtension(mod: CodeMirrorModule, first: number, lastCited: number): Extension {
  return mod.EditorView.decorations.compute(["doc"], (state) => {
    const last = state.doc.lines;
    const start = Math.min(Math.max(first, 1), last);
    const end = Math.min(Math.max(lastCited, start), last);
    const mark = mod.Decoration.line({ class: "cm-linked-line" });
    const ranges = [];
    for (let line = start; line <= end; line++) ranges.push(mark.range(state.doc.line(line).from));
    return mod.Decoration.set(ranges);
  });
}

async function loadLanguageExtension(path: string): Promise<Extension | undefined> {
  const ext = path.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "js":
    case "jsx":
    case "mjs":
    case "cjs":
      return (await import("@codemirror/lang-javascript")).javascript({ jsx: true });
    case "ts":
    case "tsx":
      return (await import("@codemirror/lang-javascript")).javascript({ jsx: true, typescript: true });
    case "json":
      return (await import("@codemirror/lang-json")).json();
    case "md":
    case "mdx":
      return (await import("@codemirror/lang-markdown")).markdown();
    case "css":
      return (await import("@codemirror/lang-css")).css();
    case "html":
      return (await import("@codemirror/lang-html")).html();
    case "py":
      return (await import("@codemirror/lang-python")).python();
    case "rs":
      return (await import("@codemirror/lang-rust")).rust();
    default:
      return undefined;
  }
}

/** The query for one worktree file as it is on disk, for `useQueries` callers
 * that read several at once and must share `useWorktreeFile`'s cache entry. */
export function worktreeFileQuery(worktreeId: string, path: string) {
  return {
    queryKey: qk.file(worktreeId, path),
    queryFn: ({ signal }: { signal: AbortSignal }) => readFile(worktreeId, path, signal),
    // A file a snapshot names as changed is expired when the summary moves;
    // one it never names (gitignored output, `.env*`) is reread when reopened
    // after this long. Until then, reopening a file is a cache read.
    staleTime: 30_000,
    gcTime: 30 * 60_000,
  };
}

/** One worktree file as it is on disk, shared by the file tab and an image's diff. */
export function useWorktreeFile(worktreeId: string, path: string, enabled = true) {
  return useQuery({ ...worktreeFileQuery(worktreeId, path), enabled });
}

/** A data URL for a file the worker returned as a previewable image, built
 * once per response rather than once per render: it can run to megabytes. */
export function useFileImageSource(file: FileContentResponse | undefined): string | null {
  return useMemo(
    () => file?.binary && file.mediaType && file.content ? `data:${file.mediaType};base64,${file.content}` : null,
    [file],
  );
}

export function FileImage({ src, path }: { src: string; path: string }) {
  return (
    <div className="attachment-preview-image">
      <img src={src} alt={path.split("/").pop() ?? path} />
    </div>
  );
}

/** A worktree file, editable when `editable` is set. An unsaved draft is
 * shown over whatever the worker has, and even when the worker cannot be
 * reached: an edit is never hidden behind a failed re-read. */
export function CodeViewer({ worktreeId, path, lines, editable = false }: { worktreeId: string; path: string; lines?: FileLineRange | null; editable?: boolean }) {
  // The editor chunk and the language pack load alongside the file rather
  // than after it, so the first open is one wait, not three in a row.
  useEffect(() => {
    void loadCodeMirror();
    void loadLanguageExtension(path);
  }, [path]);
  const fileQuery = useWorktreeFile(worktreeId, path);
  const image = useFileImageSource(fileQuery.data);
  const draft = useFileDraft(worktreeId, path);
  const onShown = () => perfMeasure("rivets:click-file→content", `rivets:click-file:${path}`);

  if (draft && editable) {
    return <EditableFile worktreeId={worktreeId} path={path} base={draft.base} text={draft.text} lines={lines} onShown={onShown} />;
  }

  if (fileQuery.isPending) {
    return (
      <div style={{ padding: 14 }}>
        <Skeleton height={14} />
        <div style={{ height: 8 }} />
        <Skeleton height={14} width="80%" />
        <div style={{ height: 8 }} />
        <Skeleton height={14} width="60%" />
      </div>
    );
  }

  if (fileQuery.isError || fileQuery.data?.error) {
    return <EmptyState title="Could not load file" hint={fileQuery.data?.error ?? "The worker may be offline."} />;
  }

  if (image) return <FileImage src={image} path={path} />;

  if (fileQuery.data?.binary) {
    return <EmptyState title="Binary file" hint={path} />;
  }

  const content = fileQuery.data?.content ?? "";
  if (editable) return <EditableFile worktreeId={worktreeId} path={path} base={content} text={content} lines={lines} onShown={onShown} />;
  return <CodeContent path={path} content={content} lines={lines} onShown={onShown} />;
}

function EditableFile({ worktreeId, path, base, text, lines, onShown }: {
  worktreeId: string; path: string; base: string; text: string; lines?: FileLineRange | null; onShown: () => void;
}) {
  const edit = useFileDrafts((state) => state.edit);
  const lineBreak = lineBreakOf(base);
  return (
    <CodeContent
      path={path}
      content={text}
      lines={lines}
      onShown={onShown}
      onChange={(value) => edit(worktreeId, path, base, withLineBreak(value, lineBreak))}
      onSave={() => void saveFileDraft(worktreeId, path)}
    />
  );
}

/** Highlighted text, for repository files and text attachments alike.
 * Read-only unless given `onChange`; `onSave` is bound to ⌘S / Ctrl+S. */
export function CodeContent({ path, content, lines, onShown, onChange, onSave }: {
  path: string;
  content: string;
  lines?: FileLineRange | null;
  onShown?: () => void;
  onChange?: (value: string) => void;
  onSave?: () => void;
}) {
  const [mod, setMod] = useState<CodeMirrorModule | null>(cachedCodeMirror);
  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [view, setView] = useState<EditorView | null>(null);
  const activeTheme = useActiveTheme();
  const dark = activeTheme.appearance === "dark";
  // The Rivets themes keep the viewer's own CodeMirror palettes — its default
  // look predates themes, and the native viewers keep theirs the same way
  // (`RivetsThemeDefinition.highlightColors`). Every other theme brings its
  // own syntax colours.
  const ownPalette = activeTheme.family === "Rivets";

  useEffect(() => {
    let cancelled = false;
    loadCodeMirror().then((loaded) => {
      if (!cancelled) setMod(loaded);
    });
    loadLanguageExtension(path).then((ext) => {
      if (!cancelled) setExtensions(ext ? [ext] : []);
    });
    return () => {
      cancelled = true;
    };
  }, [path]);

  const lineStart = lines?.start;
  const lineEnd = lines?.end;
  // Held in a ref so a new callback each render does not rebuild the
  // extensions, which would reconfigure the editor on every keystroke.
  const save = useRef(onSave);
  save.current = onSave;
  const saves = Boolean(onSave);
  const allExtensions = useMemo(() => {
    if (!mod) return extensions;
    const all = [...extensions];
    if (lineStart !== undefined && lineEnd !== undefined) all.push(linkedLinesExtension(mod, lineStart, lineEnd));
    if (saves) all.push(mod.Prec.highest(mod.keymap.of([{ key: "Mod-s", preventDefault: true, run: () => { save.current?.(); return true; } }])));
    return all;
  }, [extensions, mod, lineStart, lineEnd, saves]);

  // Each click on a link scrolls again, even to lines already in view, but a
  // snapshot refresh of the same file leaves the reader where they are.
  const request = lines?.request;
  useEffect(() => {
    if (!mod || !view || lineStart === undefined) return;
    const line = view.state.doc.line(Math.min(Math.max(lineStart, 1), view.state.doc.lines));
    view.dispatch({ effects: mod.EditorView.scrollIntoView(line.from, { y: "center" }) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mod, view, request]);

  useEffect(() => {
    if (view) onShown?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const theme = useMemo(
    () => (ownPalette ? (dark ? "dark" : "light") : mod ? editorThemeExtension(mod, dark) : undefined),
    [mod, dark, ownPalette],
  );

  if (!mod) {
    return <Skeleton height={200} />;
  }
  const CM: CodeMirrorComponent = mod.default;

  return (
    <CM
      // CodeMirror splits `\r\n` itself but hands back `\n`; given the same
      // form it reports, it never mistakes its own text for a new value and
      // resets the cursor.
      value={onChange ? content.replace(/\r\n/g, "\n") : content}
      editable={Boolean(onChange)}
      readOnly={!onChange}
      onChange={onChange}
      theme={theme}
      extensions={allExtensions}
      onCreateEditor={setView}
      basicSetup={{ lineNumbers: true, foldGutter: true, highlightActiveLine: Boolean(onChange) }}
      style={{ fontSize: "var(--mono-base)", height: "100%" }}
      height="100%"
    />
  );
}
